import React, { useState, useEffect } from 'react';
import { campaignAPI, leadAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import { Megaphone, Users, Plus, Download, Edit, Trash2, Eye, MousePointerClick, TrendingUp, Activity, Mail, Phone, Calendar, Tag, BarChart3 } from 'lucide-react';
import './Campaigns.css';
import { formatDate } from '../../utils/dateFormat';

const Campaigns = () => {
    const selectedBranch = useBranchStore((state) => state.selectedBranch);
    const [activeTab, setActiveTab] = useState('campaigns');
    const [campaigns, setCampaigns] = useState([]);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        if (activeTab === 'campaigns') fetchCampaigns();
        else fetchLeads();
    }, [selectedBranch, activeTab]);

    const fetchCampaigns = async () => {
        try {
            setLoading(true);
            const res = await campaignAPI.getAll({ branchId: selectedBranch?._id });
            setCampaigns(res.data.data.campaigns || []);
        } catch (error) {
            console.error('Failed to fetch campaigns', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeads = async () => {
        try {
            setLoading(true);
            const res = await leadAPI.getAll({ branchId: selectedBranch?._id });
            setLeads(res.data.data.leads || []);
        } catch (error) {
            console.error('Failed to fetch leads', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExportLeads = async () => {
        try {
            const response = await leadAPI.export({ branchId: selectedBranch?._id });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `leads_${new Date().toISOString()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Leads exported successfully');
        } catch (error) {
            toast.error('Failed to export leads');
        }
    };

    // Filter campaigns
    const filteredCampaigns = campaigns.filter(campaign => {
        const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || campaign.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    // Filter leads
    const filteredLeads = leads.filter(lead => {
        const name = lead.data?.name || lead.data?.fullName || '';
        const email = lead.data?.email || '';
        const phone = lead.data?.phone || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            phone.toLowerCase().includes(searchTerm.toLowerCase());
    });

    // Stats for campaigns
    const campaignStats = {
        total: campaigns.length,
        active: campaigns.filter(c => c.status === 'active').length,
        totalViews: campaigns.reduce((sum, c) => sum + (c.metrics?.views || 0), 0),
        totalClicks: campaigns.reduce((sum, c) => sum + (c.metrics?.clicks || 0), 0),
    };

    // Stats for leads
    const leadStats = {
        total: leads.length,
        today: leads.filter(l => {
            const today = new Date().toDateString();
            return new Date(l.createdAt).toDateString() === today;
        }).length,
    };

    return (
        <div className="campaigns-page">
            {/* Header */}
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <Megaphone className="title-icon" />
                            Marketing & Campaigns
                        </h1>
                        <p className="page-subtitle-modern">
                            Manage marketing campaigns and track captured leads from your WiFi portal
                        </p>
                    </div>

                    {activeTab === 'campaigns' && (
                        <button className="btn-primary-modern">
                            <Plus size={20} />
                            New Campaign
                        </button>
                    )}
                    {activeTab === 'leads' && (
                        <button onClick={handleExportLeads} className="btn-secondary-modern">
                            <Download size={20} />
                            Export CSV
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs-container">
                <button
                    onClick={() => setActiveTab('campaigns')}
                    className={`tab-btn ${activeTab === 'campaigns' ? 'active' : ''}`}
                >
                    <Megaphone size={18} />
                    Campaigns
                    <span className="tab-badge">{campaigns.length}</span>
                </button>
                <button
                    onClick={() => setActiveTab('leads')}
                    className={`tab-btn ${activeTab === 'leads' ? 'active' : ''}`}
                >
                    <Users size={18} />
                    Captured Leads
                    <span className="tab-badge">{leads.length}</span>
                </button>
            </div>

            {/* Stats Grid */}
            {activeTab === 'campaigns' ? (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon blue">
                            <Megaphone size={24} />
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{campaignStats.total}</div>
                            <div className="stat-label">Total Campaigns</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon green">
                            <Activity size={24} />
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{campaignStats.active}</div>
                            <div className="stat-label">Active Campaigns</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon purple">
                            <Eye size={24} />
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{campaignStats.totalViews}</div>
                            <div className="stat-label">Total Views</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon orange">
                            <MousePointerClick size={24} />
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{campaignStats.totalClicks}</div>
                            <div className="stat-label">Total Clicks</div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon blue">
                            <Users size={24} />
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{leadStats.total}</div>
                            <div className="stat-label">Total Leads</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon green">
                            <TrendingUp size={24} />
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{leadStats.today}</div>
                            <div className="stat-label">Today's Leads</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon purple">
                            <BarChart3 size={24} />
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">
                                {leadStats.total > 0 ? ((leadStats.today / leadStats.total) * 100).toFixed(1) : 0}%
                            </div>
                            <div className="stat-label">Today's Rate</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="filters-section">
                <div className="search-box-modern">
                    <i className="fas fa-search"></i>
                    <input
                        type="text"
                        placeholder={activeTab === 'campaigns' ? 'Search campaigns...' : 'Search leads by name, email, or phone...'}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {activeTab === 'campaigns' && (
                    <div className="filter-group">
                        <select
                            className="filter-select"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="draft">Draft</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Content */}
            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading {activeTab}...</p>
                </div>
            ) : activeTab === 'campaigns' ? (
                filteredCampaigns.length === 0 ? (
                    <div className="empty-state-modern">
                        <div className="empty-icon">
                            <Megaphone size={40} />
                        </div>
                        <h3>No Campaigns Found</h3>
                        <p className="text-gray-600">
                            {searchTerm || filterStatus !== 'all'
                                ? 'No campaigns match your filters. Try adjusting your search.'
                                : 'Create your first marketing campaign to start capturing leads from your WiFi portal.'}
                        </p>

                    </div>
                ) : (
                    <div className="campaigns-grid">
                        {filteredCampaigns.map(campaign => (
                            <div key={campaign._id} className="campaign-card">
                                <div className="campaign-card-header">
                                    <div className="campaign-icon">
                                        <Megaphone size={24} />
                                    </div>
                                    <div className="campaign-info">
                                        <div className="campaign-name">{campaign.name}</div>
                                        <div className="campaign-type">{campaign.type.replace('_', ' ')}</div>
                                    </div>
                                    <div className="campaign-actions">
                                        <button className="action-btn edit" title="Edit campaign">
                                            <Edit size={16} />
                                        </button>
                                        <button className="action-btn delete" title="Delete campaign">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>

                                <div className="campaign-card-body">
                                    <div className="status-badge-container">
                                        <span className={`status-badge ${campaign.status}`}>
                                            <span className="badge-dot"></span>
                                            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                                        </span>
                                    </div>

                                    <div className="campaign-metrics">
                                        <div className="metric-item">
                                            <Eye size={16} />
                                            <span className="metric-label">Views</span>
                                            <span className="metric-value">{campaign.metrics?.views || 0}</span>
                                        </div>
                                        <div className="metric-item">
                                            <MousePointerClick size={16} />
                                            <span className="metric-label">Clicks</span>
                                            <span className="metric-value">{campaign.metrics?.clicks || 0}</span>
                                        </div>
                                        <div className="metric-item">
                                            <TrendingUp size={16} />
                                            <span className="metric-label">CTR</span>
                                            <span className="metric-value">
                                                {campaign.metrics?.views > 0
                                                    ? ((campaign.metrics.clicks / campaign.metrics.views) * 100).toFixed(1)
                                                    : 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="campaign-card-footer">
                                    <div className="footer-item">
                                        <Calendar size={14} />
                                        <span>Created {formatDate(campaign.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : (
                filteredLeads.length === 0 ? (
                    <div className="empty-state-modern">
                        <div className="empty-icon">
                            <Users size={40} />
                        </div>
                        <h3>No Leads Captured Yet</h3>
                        <p className="text-gray-600">
                            {searchTerm
                                ? 'No leads match your search. Try a different query.'
                                : 'Start capturing leads by creating and activating your marketing campaigns.'}
                        </p>
                    </div>
                ) : (
                    <div className="leads-grid">
                        {filteredLeads.map(lead => (
                            <div key={lead._id} className="lead-card">
                                <div className="lead-card-header">
                                    <div className="lead-avatar">
                                        <Users size={24} />
                                    </div>
                                    <div className="lead-info">
                                        <div className="lead-name">{lead.data?.name || lead.data?.fullName || 'Guest User'}</div>
                                        <div className="lead-source">{lead.campaignId?.name || 'Direct'}</div>
                                    </div>
                                </div>

                                <div className="lead-card-body">
                                    {lead.data?.email && (
                                        <div className="lead-detail">
                                            <Mail size={16} />
                                            <span>{lead.data.email}</span>
                                        </div>
                                    )}
                                    {lead.data?.phone && (
                                        <div className="lead-detail">
                                            <Phone size={16} />
                                            <span>{lead.data.phone}</span>
                                        </div>
                                    )}
                                    {!lead.data?.email && !lead.data?.phone && (
                                        <div className="lead-detail empty">
                                            <span>No contact information provided</span>
                                        </div>
                                    )}
                                </div>

                                <div className="lead-card-footer">
                                    <div className="footer-item">
                                        <Calendar size={14} />
                                        <span>{formatDate(lead.createdAt)}</span>
                                    </div>
                                    <div className="footer-item">
                                        <Tag size={14} />
                                        <span>Lead</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}
        </div>
    );
};

export default Campaigns;
